import { useEffect, useMemo, useRef, useState } from "react";
import { createConsumer } from "@rails/actioncable";
import { AlertCircle, Download, Info, Loader2 } from "lucide-react";
import { sendSandboxMessage, showSandboxConversation } from "@/api/sandbox";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";
import { SandboxMarkdown } from "@/components/sandbox/SandboxMarkdown";
import { SandboxPendingApprovals } from "@/components/sandbox/SandboxPendingApprovals";
import { SandboxConversationsList } from "@/components/sandbox/SandboxConversationsList";
import { SandboxConversationsProvider, useSandboxConversations } from "@/context/SandboxConversationsContext";
import AiDisclaimer from "@/components/AiDisclaimer";
import type { SandboxConversationDetail, SandboxMessage, SandboxPendingApproval } from "@/types/sandbox";

const REGENERATE_COOLDOWN_MS = 3_000;
// Matches SandboxAgentCompletionWorker::LOCK_EXPIRY: past this, the server
// treats a pending prompt as abandoned and accepts a new one.
const ABANDONED_PROMPT_MS = 10 * 60 * 1000;
const SANDBOX_SUGGESTED_PROMPTS = [
  "Write a friendly email letting someone know I received their request and will follow up soon.",
  "Make this message sound more professional: [paste your draft here]",
  "Help me write a short agenda for a 30-minute team meeting.",
  "Turn these rough notes into a clear to-do list: [paste your notes here]",
  "Write a polite response to someone who is upset and wants an update.",
  "Give me three clearer ways to say this sentence: [paste sentence here]",
  "Help me draft a simple thank-you email after a meeting.",
  "Create a short checklist for planning a small office event.",
];

// Sandbox sends prompts straight to the model with no retrieval, so staff
// must not mistake its answers for Research Assistant's sourced ones.
const NOT_RESEARCH_ASSISTANT_NOTICE =
  "Sandbox is not Research Assistant. It talks to the AI model directly and does not search or use your city's documents, so answers are not based on city records.";

function exportConversation(conversation: SandboxConversationDetail) {
  const content = [
    `Title: ${conversation.title}`,
    `Model: ${conversation.model}`,
    "",
    ...conversation.sandbox_messages.map((message) => {
      const role = message.role.toUpperCase();
      return `[${role}] ${message.created_at}\n${message.content}`;
    }),
  ].join("\n\n");

  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `conversation-${conversation.id}.txt`;
  link.click();
  URL.revokeObjectURL(url);
}

// Pending means the newest turn is the user's, matching the server: an
// abandoned prompt that was retried leaves two user rows before one reply.
function pendingUserMessages(messages: SandboxMessage[]) {
  return messages.at(-1)?.role === "user" ? 1 : 0;
}

// In the app shell the thread list lives in the main sidebar, which shares
// SandboxConversationsProvider with this pane. The standalone /sandbox page
// has no app sidebar, so it supplies the provider and the list itself.
export default function SandboxChatPage({ embedded = false }: { embedded?: boolean }) {
  if (embedded) return <SandboxChatPane />;

  return (
    <SandboxConversationsProvider>
      <div className="grid h-screen min-h-0 grid-cols-[260px_1fr] overflow-hidden bg-background">
        <aside className="min-h-0 overflow-y-auto border-r border-border bg-card p-2">
          <SandboxConversationsList />
        </aside>
        <SandboxChatPane />
      </div>
    </SandboxConversationsProvider>
  );
}

function SandboxChatPane() {
  const { toast } = useToast();
  const { activeConversationId: selectedConversationId, archiveConversation, updateConversationTitle } = useSandboxConversations();
  const [activeConversation, setActiveConversation] = useState<SandboxConversationDetail | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [lastRegeneratedAt, setLastRegeneratedAt] = useState<number | null>(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [isImprovingPrompt, setIsImprovingPrompt] = useState(false);
  const regenerateCooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const activeConversationId = activeConversation?.id ?? null;

  useEffect(() => {
    // The pane sends and archives against the open thread, so the previous
    // one closes as soon as the selection moves, not when the next one loads.
    setActiveConversation(null);
    setError(null);
    if (selectedConversationId === null) {
      // A load superseded by this change never clears its own loading state.
      setIsLoadingConversation(false);
      return;
    }

    // A slower response for a thread the user has already left must not
    // replace the one they selected after it.
    let superseded = false;
    setIsLoadingConversation(true);
    void (async () => {
      try {
        const detail = await showSandboxConversation(selectedConversationId);
        if (!superseded) setActiveConversation(detail);
      } catch (e) {
        if (superseded) return;
        // Leaving the previous thread open would send prompts to a thread
        // other than the one highlighted in the sidebar.
        setActiveConversation(null);
        setError(e instanceof Error ? e.message : "Failed to load conversation");
      } finally {
        if (!superseded) setIsLoadingConversation(false);
      }
    })();

    return () => {
      superseded = true;
    };
  }, [selectedConversationId]);

  useEffect(() => {
    return () => {
      if (regenerateCooldownRef.current) {
        clearTimeout(regenerateCooldownRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!activeConversationId) return;

    const consumer = createConsumer();
    const subscription = consumer.subscriptions.create(
      { channel: "SandboxChannel", conversation_id: activeConversationId },
      {
        received: (payload: any) => {
          if (payload.type === "message") {
            const message: SandboxMessage = {
              id: payload.id,
              role: payload.role,
              content: payload.content,
              created_at: payload.created_at,
            };
            setActiveConversation((previous) =>
              previous && previous.id === activeConversationId
                ? { ...previous, sandbox_messages: [...previous.sandbox_messages, message], pending_approvals: [] }
                : previous,
            );
          } else if (payload.type === "error") {
            const message: SandboxMessage = {
              id: payload.id,
              role: "assistant",
              content: payload.content ?? "I couldn't complete that request.",
              error_code: payload.error_code,
              created_at: payload.created_at,
            };
            setActiveConversation((previous) =>
              previous && previous.id === activeConversationId
                ? { ...previous, sandbox_messages: [...previous.sandbox_messages, message], pending_approvals: [] }
                : previous,
            );
          } else if (payload.type === "approval_required") {
            setPendingApprovals(payload.pending_approvals);
          } else if (payload.type === "title_update") {
            updateConversationTitle(payload.id, payload.title);
            setActiveConversation((previous) =>
              previous && previous.id === payload.id ? { ...previous, title: payload.title } : previous,
            );
          }
        },
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [activeConversationId, updateConversationTitle]);

  const setPendingApprovals = (pending_approvals: SandboxPendingApproval[]) =>
    setActiveConversation((previous) => (previous ? { ...previous, pending_approvals } : previous));
  const pendingApprovals = activeConversation?.pending_approvals ?? [];

  const sendMessage = async (content: string) => {
    if (!activeConversation) return;
    const prompt = content.trim();
    if (!prompt) return;

    setError(null);
    const optimisticMessage: SandboxMessage = {
      id: Date.now(),
      role: "user",
      content: prompt,
      created_at: new Date().toISOString(),
    };
    setActiveConversation((previous) =>
      previous ? { ...previous, sandbox_messages: [...previous.sandbox_messages, optimisticMessage] } : previous,
    );
    setDraft("");

    try {
      await sendSandboxMessage(activeConversation.id, prompt);
    } catch (e) {
      // The prompt never reached the thread, so it is not pending.
      setActiveConversation((previous) =>
        previous
          ? {
              ...previous,
              sandbox_messages: previous.sandbox_messages.filter((message) => message.id !== optimisticMessage.id),
            }
          : previous,
      );
      setError(e instanceof Error ? e.message : "Failed to send message");
    }
  };

  const regenerateLastResponse = async () => {
    if (!activeConversation) return;
    if (lastRegeneratedAt && Date.now() - lastRegeneratedAt < REGENERATE_COOLDOWN_MS) {
      setError("Please wait a moment before regenerating again.");
      return;
    }
    if (pendingCount > 0) {
      setError("Please wait for queued responses to finish before regenerating.");
      return;
    }
    const lastUserMessage = [...activeConversation.sandbox_messages].reverse().find((message) => message.role === "user");
    if (!lastUserMessage) return;
    setLastRegeneratedAt(Date.now());
    regenerateCooldownRef.current = setTimeout(() => setLastRegeneratedAt(null), REGENERATE_COOLDOWN_MS);
    await sendMessage(lastUserMessage.content);
  };

  const archiveActiveConversation = async () => {
    if (!activeConversation) return;
    try {
      await archiveConversation(activeConversation.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to archive conversation");
    }
  };

  const lastAssistantMessage = useMemo(() => {
    if (!activeConversation) return null;
    return [...activeConversation.sandbox_messages].reverse().find((message) => message.role === "assistant") ?? null;
  }, [activeConversation]);

  const pendingCount = useMemo(
    () => pendingUserMessages(activeConversation?.sandbox_messages || []),
    [activeConversation?.sandbox_messages],
  );
  const regenerateDisabled = !activeConversation || pendingCount > 0 || !lastAssistantMessage;
  // One prompt at a time: the server rejects a prompt while a reply is
  // pending, unless that prompt is old enough to count as abandoned.
  const lastMessage = activeConversation?.sandbox_messages.at(-1);
  const replyPending =
    pendingCount > 0 &&
    !(lastMessage?.role === "user" && Date.now() - Date.parse(lastMessage.created_at) > ABANDONED_PROMPT_MS);
  const canSend = Boolean(activeConversation && draft.trim() && !replyPending);
  const canImprovePrompt = Boolean(activeConversation && draft.trim() && !isImprovingPrompt);
  const shouldShowSuggestedPrompts = Boolean(
    activeConversation &&
      activeConversation.sandbox_messages.length === 0 &&
      !isLoadingConversation,
  );

  const suggestedPrompts = useMemo(
    () => SANDBOX_SUGGESTED_PROMPTS.slice(0, 3),
    [],
  );

  const copyMessage = async (content: string) => {
    try {
      await navigator.clipboard?.writeText(content);
      toast({
        title: "Response copied",
        description: "The assistant response was copied to your clipboard.",
      });
    } catch {
      setError("Unable to copy the response to clipboard.");
    }
  };

  const selectSuggestedPrompt = (prompt: string) => {
    setDraft(prompt);
    draftTextareaRef.current?.focus();
  };

  const improvePrompt = async () => {
    const prompt = draft.trim();
    if (!prompt) return;

    setError(null);
    setIsImprovingPrompt(true);
    try {
      const response = await fetch("/api/v1/completions/improve_prompt", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({
          improve_prompt: {
            text: prompt,
          },
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to improve prompt");
      }

      setDraft(data.response);
      draftTextareaRef.current?.focus();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to improve prompt");
    } finally {
      setIsImprovingPrompt(false);
    }
  };

  return (
    <div className="grid h-full min-h-0 grid-cols-1 overflow-hidden bg-background">
      <main className="flex min-h-0 flex-col overflow-hidden">
        <header className="flex shrink-0 items-center justify-between border-b border-border bg-card px-4 py-3">
          <div>
            <h2 className="font-semibold">{activeConversation?.title || "Select a conversation"}</h2>
            <p className="text-xs text-muted-foreground">Conversations are retained according to workspace policy</p>
          </div>
          {activeConversation && (
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Download thread"
                      onClick={() => exportConversation(activeConversation)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Download thread</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button type="button" variant="outline" onClick={archiveActiveConversation}>
                Archive
              </Button>
            </div>
          )}
        </header>

        <div className="shrink-0 border-b border-border bg-card px-4 py-2">
          <Alert
            role="note"
            data-testid="sandbox-not-research-assistant"
            className="border-amber-200 bg-amber-50 py-2 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200 [&>svg]:top-2.5 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400"
          >
            <Info className="h-4 w-4" />
            <AlertDescription>{NOT_RESEARCH_ASSISTANT_NOTICE}</AlertDescription>
          </Alert>
        </div>

        {error && (
          <div className="shrink-0 border-b border-border p-3">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        <ScrollArea className="min-h-0 flex-1 overflow-hidden">
          <section className="space-y-3 p-4">
            {!activeConversation && !isLoadingConversation && (
              <Card className="border-dashed">
                <CardContent className="p-6 text-sm text-muted-foreground">Pick a conversation to begin.</CardContent>
              </Card>
            )}

            {isLoadingConversation && (
              <>
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </>
            )}

            {shouldShowSuggestedPrompts && (
              <div className="space-y-1 p-2">
                <p className="text-sm text-muted-foreground">Suggested questions:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedPrompts.map((prompt) => (
                    <Button
                      key={prompt}
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="h-auto whitespace-normal break-words text-left text-sm text-muted-foreground hover:text-foreground"
                      onClick={() => selectSuggestedPrompt(prompt)}
                    >
                      {prompt}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Speakers are told apart by placement and card styling, never a text label. */}
            {activeConversation?.sandbox_messages.map((message) => (
              <Card
                key={`${message.id}-${message.created_at}`}
                data-testid="sandbox-message"
                className={cn(
                  message.role === "assistant" ? "mr-auto w-full bg-card" : "ml-auto w-fit max-w-[85%] break-words border-primary/20 bg-muted/40",
                )}
              >
                <CardContent className="p-3">
                  {message.role === "assistant" && (
                    <div className="mb-2 flex items-center justify-end text-xs text-muted-foreground">
                      <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => void copyMessage(message.content)}>
                        Copy
                      </Button>
                    </div>
                  )}
                  <SandboxMarkdown content={message.content} />
                </CardContent>
              </Card>
            ))}

            {activeConversation && (
              <SandboxPendingApprovals
                conversationId={activeConversation.id}
                approvals={pendingApprovals}
                onChange={setPendingApprovals}
              />
            )}

            {/* Derived from the saved transcript, so it survives a refresh. No duration, tokens, or cost. */}
            {pendingCount > 0 && pendingApprovals.length === 0 && (
              <Card role="status" className="border-primary/30 bg-primary/5">
                <CardContent className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                  <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                  Thinking...
                </CardContent>
              </Card>
            )}
          </section>
        </ScrollArea>

        {activeConversation && (
          <footer className="shrink-0 space-y-2 border-t border-border bg-card p-4">
            <Textarea
              ref={draftTextareaRef}
              placeholder="Ask a question..."
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={() => void regenerateLastResponse()} disabled={regenerateDisabled}>
                  Regenerate Last Response
                </Button>
                {pendingCount > 0 && <Badge variant="secondary">Queue: {pendingCount}</Badge>}
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" variant="secondary" onClick={() => void improvePrompt()} disabled={!canImprovePrompt}>
                  {isImprovingPrompt ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Improving...
                    </>
                  ) : (
                    "Improve My Prompt"
                  )}
                </Button>
                <Button type="button" onClick={() => void sendMessage(draft)} disabled={!canSend}>
                  Send
                </Button>
              </div>
            </div>
            {lastAssistantMessage?.error_code && (
              <p className="text-xs text-destructive">Last response error: {lastAssistantMessage.error_code}</p>
            )}
            <AiDisclaimer className="text-center" />
          </footer>
        )}
      </main>
    </div>
  );
}
